/**
 * Predefined control action templates for the Control Panel.
 * Hardcoded for expo MVP — post-expo these move to DB-backed automation_templates.
 */
import type { SettingsChange } from './control-types';

export interface ControlTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'quick_action' | 'optimization' | 'system_control';
  risk_level: 'low' | 'medium' | 'high';
  settings_changes: SettingsChange[];
  supported_providers: string[];
  coming_soon?: boolean;
}

export const CONTROL_TEMPLATES: ControlTemplate[] = [
  // ═══════════════════════════════════════════════════════════
  // SOL-ARK TEMPLATES
  // ═══════════════════════════════════════════════════════════

  // --- Sol-Ark Quick Actions ---
  {
    id: 'solark_force_grid_charge',
    name: 'Force Grid Charge',
    description: 'Enable grid charging to replenish battery from the grid.',
    icon: 'Zap',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'genChargeOn', value: 1 },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_disable_grid_charge',
    name: 'Disable Grid Charge',
    description: 'Stop charging battery from the grid. Battery charges from solar only.',
    icon: 'ZapOff',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'genChargeOn', value: 0 },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_raise_reserve',
    name: 'Raise Battery Reserve',
    description: 'Increase shutdown voltage by 1V to protect battery during heavy use.',
    icon: 'ShieldPlus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'batteryShutdownVolt', value: null, previous_value: null },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_lower_reserve',
    name: 'Lower Battery Reserve',
    description: 'Decrease shutdown voltage by 1V to access more battery capacity.',
    icon: 'ShieldMinus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'batteryShutdownVolt', value: null, previous_value: null },
    ],
    supported_providers: ['solark'],
  },

  // --- Sol-Ark Optimization Modes ---
  {
    id: 'solark_storm_prep',
    name: 'Storm Preparation',
    description: 'Raise reserve voltage and enable grid charging to maximize stored energy before a storm.',
    icon: 'CloudLightning',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'batteryShutdownVolt', value: 48 },
      { field: 'genChargeOn', value: 1 },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_self_consumption',
    name: 'Self-Consumption',
    description: 'Standard home mode. Solar charges battery and powers loads. No grid export.',
    icon: 'Home',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'zeroExportPower', value: 0 },
      { field: 'genChargeOn', value: 0 },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_grid_export',
    name: 'Grid Export',
    description: 'Allow excess solar to export to the grid. Useful for net metering or TOU arbitrage.',
    icon: 'ArrowUpFromLine',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'zeroExportPower', value: 5000 },
    ],
    supported_providers: ['solark'],
  },
  {
    id: 'solark_solar_harvest',
    name: 'Solar Harvest',
    description: 'Lower reserve to minimize curtailment when battery is full. Maximizes solar utilization.',
    icon: 'Sun',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'batteryShutdownVolt', value: 44 },
      { field: 'zeroExportPower', value: 5000 },
    ],
    supported_providers: ['solark'],
  },

  // ═══════════════════════════════════════════════════════════
  // EG4 TEMPLATES
  // Fields map to Modbus registers: HOLD_ → remoteSet/write,
  // FUNC_ → remoteSet/functionControl
  // ═══════════════════════════════════════════════════════════

  // --- EG4 Quick Actions ---
  {
    id: 'eg4_force_grid_charge',
    name: 'Force Grid Charge',
    description: 'Enable AC charging from the grid to replenish battery.',
    icon: 'Zap',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'FUNC_AC_CHARGE', value: 1 },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_disable_grid_charge',
    name: 'Disable Grid Charge',
    description: 'Stop AC charging from the grid. Battery charges from solar only.',
    icon: 'ZapOff',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'FUNC_AC_CHARGE', value: 0 },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_raise_reserve',
    name: 'Raise Battery Reserve',
    description: 'Increase discharge cutoff SoC by 10% to protect battery during heavy use.',
    icon: 'ShieldPlus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'HOLD_DISCHG_CUT_OFF_SOC_EOD', value: null, previous_value: null },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_lower_reserve',
    name: 'Lower Battery Reserve',
    description: 'Decrease discharge cutoff SoC by 10% to access more battery capacity.',
    icon: 'ShieldMinus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'HOLD_DISCHG_CUT_OFF_SOC_EOD', value: null, previous_value: null },
    ],
    supported_providers: ['eg4'],
  },

  // --- EG4 Optimization Modes ---
  {
    id: 'eg4_storm_prep',
    name: 'Storm Preparation',
    description: 'Max reserve SoC and enable grid charging to store maximum energy before a storm.',
    icon: 'CloudLightning',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'HOLD_DISCHG_CUT_OFF_SOC_EOD', value: 90 },
      { field: 'FUNC_AC_CHARGE', value: 1 },
      { field: 'HOLD_AC_CHARGE_SOC_LIMIT', value: 100 },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_self_consumption',
    name: 'Self-Consumption',
    description: 'Standard home mode. Solar powers loads and charges battery. No grid export.',
    icon: 'Home',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'FUNC_FEED_IN_GRID_EN', value: 0 },
      { field: 'FUNC_AC_CHARGE', value: 0 },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_grid_export',
    name: 'Grid Export',
    description: 'Enable feed-in to export excess solar to the grid for net metering or TOU arbitrage.',
    icon: 'ArrowUpFromLine',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'FUNC_FEED_IN_GRID_EN', value: 1 },
      { field: 'HOLD_FEED_IN_GRID_POWER_PERCENT', value: 100 },
    ],
    supported_providers: ['eg4'],
  },
  {
    id: 'eg4_solar_harvest',
    name: 'Solar Harvest',
    description: 'Lower reserve and enable export to minimize curtailment. Maximizes solar utilization.',
    icon: 'Sun',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'HOLD_DISCHG_CUT_OFF_SOC_EOD', value: 10 },
      { field: 'FUNC_FEED_IN_GRID_EN', value: 1 },
    ],
    supported_providers: ['eg4'],
  },

  // ═══════════════════════════════════════════════════════════
  // FRANKLINWH TEMPLATES (all coming_soon)
  // Write adapter exists but Backup Reserve API endpoint
  // needs HAR-capture verification before activation.
  // Fields map to tou_/solar_/gen_ prefixed endpoint groups.
  // ═══════════════════════════════════════════════════════════

  // --- FranklinWH Quick Actions ---
  {
    id: 'franklinwh_enable_grid_charge',
    name: 'Enable Grid Charge',
    description: 'Allow battery charging from the grid.',
    icon: 'Zap',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'tou_gridChargeEnable', value: 1 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_disable_grid_charge',
    name: 'Disable Grid Charge',
    description: 'Stop battery charging from the grid. Battery charges from solar only.',
    icon: 'ZapOff',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'tou_gridChargeEnable', value: 0 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_raise_reserve',
    name: 'Raise Backup Reserve',
    description: 'Increase backup reserve SoC by 10% to hold more energy for outages.',
    icon: 'ShieldPlus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'tou_reserveSoc', value: null, previous_value: null },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_lower_reserve',
    name: 'Lower Backup Reserve',
    description: 'Decrease backup reserve SoC by 10% to access more battery capacity.',
    icon: 'ShieldMinus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'tou_reserveSoc', value: null, previous_value: null },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },

  // --- FranklinWH Optimization Modes ---
  {
    id: 'franklinwh_storm_prep',
    name: 'Storm Preparation',
    description: 'Max reserve and enable grid charging to store maximum energy before a storm.',
    icon: 'CloudLightning',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'tou_reserveSoc', value: 100 },
      { field: 'tou_gridChargeEnable', value: 1 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_self_consumption',
    name: 'Self-Consumption',
    description: 'Self-use mode. Solar powers loads and charges battery. No grid export.',
    icon: 'Home',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'tou_workMode', value: 0 },
      { field: 'solar_agatePvExportEn', value: 0 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_grid_export',
    name: 'Grid Export',
    description: 'Enable PV export to sell excess solar to the grid.',
    icon: 'ArrowUpFromLine',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'solar_agatePvExportEn', value: 1 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },
  {
    id: 'franklinwh_backup_priority',
    name: 'Backup Priority',
    description: 'Maximize stored energy for outage protection. High reserve, grid charging enabled.',
    icon: 'Battery',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'tou_reserveSoc', value: 80 },
      { field: 'tou_gridChargeEnable', value: 1 },
    ],
    supported_providers: ['franklinwh'],
    coming_soon: true,
  },

  // ═══════════════════════════════════════════════════════════
  // SOLIS TEMPLATES
  // Fields map to CID commands via /v2/api/control endpoint.
  // work_mode CID 55 values: 1=Self-Use, 2=TOU, 3=Backup
  // ═══════════════════════════════════════════════════════════

  // --- Solis Quick Actions ---
  {
    id: 'solis_self_use_mode',
    name: 'Set Self-Use Mode',
    description: 'Switch to self-use mode. Solar powers loads, excess charges battery.',
    icon: 'Home',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'work_mode', value: 1 },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_backup_mode',
    name: 'Set Backup Mode',
    description: 'Switch to backup mode. Battery held at reserve for outage protection.',
    icon: 'Battery',
    category: 'quick_action',
    risk_level: 'low',
    settings_changes: [
      { field: 'work_mode', value: 3 },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_raise_reserve',
    name: 'Raise Backup Reserve',
    description: 'Increase backup SoC by 10% to hold more energy for outages.',
    icon: 'ShieldPlus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'backup_soc', value: null, previous_value: null },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_lower_reserve',
    name: 'Lower Backup Reserve',
    description: 'Decrease backup SoC by 10% to access more battery capacity.',
    icon: 'ShieldMinus',
    category: 'quick_action',
    risk_level: 'medium',
    settings_changes: [
      { field: 'backup_soc', value: null, previous_value: null },
    ],
    supported_providers: ['solis'],
  },

  // --- Solis Optimization Modes ---
  {
    id: 'solis_self_consumption',
    name: 'Self-Consumption',
    description: 'Self-use mode with no grid export. Solar powers loads and charges battery.',
    icon: 'Home',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'work_mode', value: 1 },
      { field: 'grid_feed_in_limit', value: 0 },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_battery_priority',
    name: 'Battery Priority',
    description: 'Backup mode with 80% reserve. Prioritizes battery for outage protection.',
    icon: 'Battery',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'work_mode', value: 3 },
      { field: 'backup_soc', value: 80 },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_grid_export',
    name: 'Grid Export',
    description: 'Self-use mode with high feed-in limit. Sell excess solar to the grid.',
    icon: 'ArrowUpFromLine',
    category: 'optimization',
    risk_level: 'low',
    settings_changes: [
      { field: 'work_mode', value: 1 },
      { field: 'grid_feed_in_limit', value: 5000 },
    ],
    supported_providers: ['solis'],
  },
  {
    id: 'solis_maximum_backup',
    name: 'Maximum Backup',
    description: 'Backup mode with 100% reserve. Maximum outage protection at the cost of solar utilization.',
    icon: 'CloudLightning',
    category: 'optimization',
    risk_level: 'medium',
    settings_changes: [
      { field: 'work_mode', value: 3 },
      { field: 'backup_soc', value: 100 },
    ],
    supported_providers: ['solis'],
  },
];

/** Get templates for a specific provider. */
export function getTemplatesForProvider(providerType: string): ControlTemplate[] {
  return CONTROL_TEMPLATES.filter(t => t.supported_providers.includes(providerType));
}

/** Get templates by category. */
export function getTemplatesByCategory(
  providerType: string
): Record<string, ControlTemplate[]> {
  const templates = getTemplatesForProvider(providerType);
  const grouped: Record<string, ControlTemplate[]> = {};
  for (const t of templates) {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  }
  return grouped;
}
